


















































































import React, { useMemo, useState } from "react";
import { FieldGroupRow, FieldGroupRowStatus, FIELD_GROUP_ROW_SORT_TIER, summarizeFieldGroupRows } from "../../../models/intake";
import "./QuickEntryFieldGroupList.scss";

// master-demo-v6.html's own 4 row-status icons (.qe-row__icon, a SEPARATE
// element from .qe-row__badge's numbered circle - not a replacement for
// it). Colors/paths ported 1:1 from the demo's inline SVGs; icon-check/
// icon-warn/icon-stop use currentColor + a CSS color class (QuickEntryFieldGroupList.scss),
// icon-user's blue/white are baked into its own paths in the demo, kept as-is.
const ICON_CHECK_SVG = (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0ZM6.23828 8.52246L3.9541 6.23828L3.19238 7L6.23828 10.0459L10.8076 5.47656L10.0459 4.71484L6.23828 8.52246Z" fill="currentColor" />
    </svg>
);
const ICON_WARN_SVG = (
    <svg viewBox="0 0 15 13" fill="none" aria-hidden="true">
        <path d="M6.40961 0.503857C6.79351 -0.167955 7.76204 -0.16795 8.14594 0.503857L14.4233 11.4882C14.804 12.1548 14.3227 12.9841 13.5551 12.9843H1.00141C0.233587 12.9843 -0.247701 12.1549 0.133246 11.4882L6.40961 0.503857ZM6.70649 10.0761V11.2187H7.84907V10.0761H6.70649ZM6.70649 3.60835V9.03804H7.84907V3.60835H6.70649Z" fill="currentColor" />
    </svg>
);
const ICON_STOP_SVG = (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0ZM6.46191 9.69238V10.7695H7.53809V9.69238H6.46191ZM6.46191 3.23047V8.61523H7.53809V3.23047H6.46191Z" fill="currentColor" />
    </svg>
);
const ICON_USER_SVG = (
    <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <circle cx="11" cy="11" r="11" fill="#5DA3E3" />
        <path d="M11 11C12.6569 11 14 9.6569 14 8C14 6.3431 12.6569 5 11 5C9.3431 5 8 6.3431 8 8C8 9.6569 9.3431 11 11 11ZM11 12.5C9 12.5 5 13.5 5 15.5V17H17V15.5C17 13.5 13 12.5 11 12.5Z" fill="white" />
    </svg>
);
// master-demo-v6-standalone-src.html's own v6-additions.js NEUTRAL_SVG - the
// hollow circle it swaps in for a row that becomes optional (e.g. the
// Authorization Date row once the selected Purpose no longer requires it -
// see that file's setAuthRequired(false) branch: an unfilled-but-not-
// required field still gets an icon, aria-label "Optional", not nothing.
// Bug fix (Spec #34102 / F1d): every row in the demo's own static markup
// (patient/requestor/service-date/etc. - confirmed by exhaustively grepping
// its qe-row__icon elements) carries SOME icon; there is no "no icon" row
// in the mockup. EmptyNotRequired previously fell through to `null` here,
// so Quick Entry's own optional/not-yet-applicable rows rendered with no
// icon at all - a real, user-visible gap from the mockup, not a deliberate
// omission (the prior doc comment's "no icon in the mockup" claim was
// unverified against the actual demo markup).
const ICON_NEUTRAL_SVG = (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" />
    </svg>
);

/**
 * Confirmed splits into two distinct icons (mockup's own icon-check vs
 * icon-user): green check when the value is still the original AI
 * extraction (isAiPreSelected), blue "user" icon once the specialist has
 * entered or edited it themselves.
 *
 * AB#35289: the green check ALSO applies when the row reports isDocSourced - the
 * Records Requested + two Service Date rows are doc-sourced but hardcode
 * isAiPreSelected=false (AB#35066: they signal AI-sourcing via the inline grounding
 * icon rather than the "AI pre-selected" chip), so keying the badge on
 * isAiPreSelected alone painted an AI-seeded value blue "user-entered" and
 * contradicted that icon. The badge is therefore
 * (isAiPreSelected || isDocSourced) while the "AI pre-selected" CHIP below stays
 * keyed on isAiPreSelected ONLY, so the chip remains off those rows. Once the
 * specialist edits such a value so it diverges from the AI set, isDocSourced goes
 * false and the badge correctly reverts to the blue "user-entered" icon.
 *
 * NeedsEntry/LowConfidence map 1:1 to the
 * mockup's red icon-stop/amber icon-warn. EmptyNotRequired (a row that's
 * either genuinely optional or not yet applicable per
 * DataEntryDetails.tsx's isQuickEntryRowApplicable) gets the mockup's own
 * neutral hollow-circle "Optional" icon (see ICON_NEUTRAL_SVG above) - not
 * no icon at all.
 *
 * NOTE on the mockup's amber "low confidence" (icon-warn) state: no row
 * mapper in this codebase produces FieldGroupRowStatus.LowConfidence today
 * (grep confirms no ConfidenceScore/ConfidenceLevel field exists anywhere
 * yet) - real per-field AI confidence scoring is sibling Spec F1b-rois's
 * (#34100) unshipped domain, not this Spec's (#34102 / F1d) to invent. This
 * function already renders icon-warn correctly for any row that DOES carry
 * LowConfidence once F1b-rois ships it.
 */
function getQuickEntryRowIcon(row: FieldGroupRow): { className: string; ariaLabel: string; svg: React.ReactNode } {
    switch (row.status) {
        case FieldGroupRowStatus.Confirmed:
            // AB#35289: green "Confirmed" check when the value is AI-pre-selected OR doc-sourced.
            // isDocSourced covers the Records Requested + Service Date rows, whose mappers hardcode
            // isAiPreSelected=false (AB#35066) - without it a doc-sourced value on those rows painted
            // blue "user-entered", contradicting the inline grounding (=) icon. The "AI pre-selected"
            // chip below stays keyed on isAiPreSelected ONLY, so it remains off these rows.
            return (row.isAiPreSelected || row.isDocSourced)
                ? { className: "icon-check", ariaLabel: "Confirmed", svg: ICON_CHECK_SVG }
                : { className: "icon-user", ariaLabel: "User-entered value", svg: ICON_USER_SVG };
        case FieldGroupRowStatus.LowConfidence:
            return { className: "icon-warn", ariaLabel: "Low confidence - click to confirm", svg: ICON_WARN_SVG };
        case FieldGroupRowStatus.NeedsEntry:
            return { className: "icon-stop", ariaLabel: "Needs entry", svg: ICON_STOP_SVG };
        case FieldGroupRowStatus.EmptyNotRequired:
        default:
            return { className: "icon-neutral", ariaLabel: "Optional", svg: ICON_NEUTRAL_SVG };
    }
}

export interface QuickEntryFieldGroupListProps {
    readonly rows: readonly FieldGroupRow[];
    /** Default ON/OFF state for the "Needs attention first" sort toggle (AC7). */
    readonly sortByAttentionDefault?: boolean;
    /**
     * Optional per-row content override (AC11). When provided and it returns
     * a non-null/non-undefined node for a given row, that node renders in
     * place of the generic label/value/chip trio - the badge number and the
     * surrounding <li> wrapper (and therefore numbering/reorder/sort
     * behavior) are unaffected. Sibling specs (F1b-rois #34100, F1c #34101,
     * F3 #34099) that don't pass this prop get the generic display
     * unchanged - this is purely additive to the FieldGroupRow contract.
     * Used by DataEntryDetails.tsx to mount the real, interactive
     * RequestTypePurposeField for the Request Type/Purpose row instead of
     * its read-only label/value text, so both the classic RightPane and the
     * Quick Entry row render the same shared component (AC11).
     */
    readonly renderRowContent?: (row: FieldGroupRow) => React.ReactNode;
    /**
     * Optional per-row adornment appended AFTER the row content (F1b-rois
     * #34100 confidence/grounding indicator, rendered at line ~250). Additive;
     * rows render unchanged when omitted.
     */
    readonly renderRowAdornment?: (row: FieldGroupRow) => React.ReactNode;
    /**
     * master-demo-v6.html's <footer class="action-footer"> - a direct child
     * of the Quick Entry panel, sibling of .qe__header and the row list
     * (confirmed via DOM inspection: it closes before </main> does). Passed
     * in by the caller (DataEntryDetails.tsx mounts QuickEntryActionFooter
     * here) rather than owned by this generic list component.
     */
    readonly footer?: React.ReactNode;
    /**
     * Acknowledge control for LowConfidence rows (Spec #34102 / F1d, AC3/
     * Business Rule 2). No row mapper produces LowConfidence yet (F1b-rois
     * #34100 owns confidence scoring) - this is forward-compatible scaffolding
     * so the mechanism already works once a sibling Spec's rows can reach
     * that status. Both props are optional and additive; omitting them
     * renders exactly as before (no acknowledge control at all).
     */
    readonly onAcknowledgeLowConfidenceRow?: (rowId: string) => void;
    readonly acknowledgedLowConfidenceIds?: ReadonlySet<string>;
    /**
     * The rows that COUNT as settled by acknowledgement, which is not always the same set as the
     * rows the specialist has ticked - a tick on a row that has since been reopened for edit is
     * still a tick, but the row is not settled while an edit is in flight (AB#35073). Only the
     * header tally reads this; the checkbox above reads acknowledgedLowConfidenceIds, because a
     * controlled checkbox MUST display the same set its onChange toggles or it goes unresponsive.
     * Defaults to acknowledgedLowConfidenceIds, so a caller that has no such distinction (and
     * every existing test) behaves exactly as before.
     */
    readonly settledLowConfidenceIds?: ReadonlySet<string>;
    /**
     * Rows whose LowConfidence is resolved by their OWN in-row control rather
     * than the generic Acknowledge checkbox (coalesce 2026-07-20). The Patient
     * Info / Requestor Info rows (Spec #34101 / F1c) reach LowConfidence on a
     * MULTIPLE match and are resolved by picking a candidate in the match grid,
     * NOT by acknowledging - so the F1d Acknowledge control must not also render
     * on them (two competing resolution mechanisms on one row). Their outstanding
     * state clears only when the match resolves to a single candidate, which is
     * exactly what the patient/requestor gate (isPatientRequestorConfirmed) also
     * requires - so the row list and the gate stay consistent.
     */
    readonly selfResolvingLowConfidenceRowIds?: ReadonlySet<string>;
}

/**
 * Shared Quick Entry field-group list renderer (Spec #34098 / F1a, AC5-AC10).
 * Sibling specs (F1b-rois #34100, F1c #34101, F3 #34099) implement their own
 * row components against the FieldGroupRow contract this list renders.
 */
const QuickEntryFieldGroupList: React.FC<QuickEntryFieldGroupListProps> = (props) => {
    const [sortByAttention, setSortByAttention] = useState(props.sortByAttentionDefault ?? false);

    const orderedRows = useMemo(() => {
        if (!sortByAttention) {
            return props.rows;
        }
        // Stable sort by tier, preserving original document order within a tier (Business Rule 8).
        return props.rows
            .map((row, index) => ({ row, index }))
            .sort((a, b) => {
                const tierDiff = FIELD_GROUP_ROW_SORT_TIER[a.row.status] - FIELD_GROUP_ROW_SORT_TIER[b.row.status];
                return tierDiff !== 0 ? tierDiff : a.index - b.index;
            })
            .map(entry => entry.row);
    }, [props.rows, sortByAttention]);

    // Badge numbers renumber contiguously (1..N) in whatever order is currently rendered (AC6).
    const numberedRows = useMemo(
        () => orderedRows.map((row, index) => ({ ...row, badgeNumber: index + 1 })),
        [orderedRows],
    );

    // Bug fix (AB#35091): non-applicable rows are excluded from both counts - see
    // summarizeFieldGroupRows in models/intake.ts for why the old count overstated progress.
    // Bug fix (AB#38158, defect 3): pass the acknowledged set so this header and the footer's
    // "Next Action Required (N)" cannot disagree - acknowledging a row used to drop the footer
    // count while the header still reported the row as needing attention.
    const { ready, attention: attn } = summarizeFieldGroupRows(props.rows, props.settledLowConfidenceIds ?? props.acknowledgedLowConfidenceIds);

    const onToggleSort = () => setSortByAttention(prev => !prev);

    return (
        <div className="qe-shell">
            <div className="qe__header">
                <div className="qe__header-top">
                    <h1 className="qe__title">Quick Entry</h1>
                    <label className="qe__sort-toggle">
                        <input type="checkbox" checked={sortByAttention} onChange={onToggleSort} />
                        Needs attention first
                    </label>
                </div>
                <p className="qe__summary">
                    {attn > 0 ? (
                        <>
                            <strong>{ready}</strong> ready to confirm{" - "}
                            <span className="qe__summary--attn">{attn} need your attention</span>
                        </>
                    ) : (
                        // Same AB#35091 denominator as `ready` above: non-applicable rows aren't
                        // things the specialist submitted. `ready === 0` here means every row is
                        // non-applicable, so there is genuinely nothing to announce as submittable.
                        ready > 0 ? `All ${ready} ready to submit` : "Nothing to confirm on this request"
                    )}
                </p>
            </div>

            <ul className="qe-row-list">
                {numberedRows.map(row => {
                    const customContent = props.renderRowContent?.(row);
                    const icon = getQuickEntryRowIcon(row);
                    return (
                        <li
                            key={row.id}
                            className="qe-row"
                            data-status={row.status}
                            data-row-id={row.id}
                        >
                            <span className="qe-row__badge">{row.badgeNumber}</span>
                            {icon ? (
                                <span className={`qe-row__icon ${icon.className}`} aria-label={icon.ariaLabel}>
                                    {icon.svg}
                                </span>
                            ) : null}
                            {customContent != null ? (
                                customContent
                            ) : (
                                <>
                                    <span className="qe-row__label">{row.label}</span>
                                    <span className="qe-row__value">{row.value}</span>
                                    {/* AB#35075: explain the status WITHOUT requiring Edit first. */}
                                    {row.statusExplanation ? (
                                        <span
                                            className="qe-row__status-info"
                                            title={row.statusExplanation}
                                            role="img"
                                            aria-label={row.statusExplanation}
                                        >&#9432;</span>
                                    ) : null}
                                    {row.isAiPreSelected ? <span className="qe-chip--ai">AI pre-selected</span> : null}
                                </>
                            )}
                            {props.renderRowAdornment ? props.renderRowAdornment(row) : null}
                            {row.status === FieldGroupRowStatus.LowConfidence && props.onAcknowledgeLowConfidenceRow && !(props.selfResolvingLowConfidenceRowIds?.has(row.id)) ? (
                                <label className="qe-row__acknowledge">
                                    <input
                                        type="checkbox"
                                        className="qe-row__acknowledge-checkbox"
                                        // Review round 3 (Mike Hamilton, PR !10065): this reads the RAW ticked set on
                                        // purpose. It is a controlled checkbox, so displaying a filtered set while
                                        // onChange toggles the unfiltered one left it stuck unchecked on any row open
                                        // for edit - the clicks landed on hidden state and only surfaced when the edit
                                        // closed. Same class of defect as AB#38158 defect 1, reintroduced by the
                                        // round-2 counter fix. The header's tally keeps the filtered set
                                        // (settledLowConfidenceIds) - that distinction belongs to the count, not here.
                                        checked={props.acknowledgedLowConfidenceIds?.has(row.id) ?? false}
                                        onChange={() => props.onAcknowledgeLowConfidenceRow!(row.id)}
                                    />
                                    Acknowledge
                                </label>
                            ) : null}
                        </li>
                    );
                })}
            </ul>

            {props.footer}
        </div>
    );
};

export default QuickEntryFieldGroupList;